import React, { useCallback } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import { useFlags } from '@atlaskit/flag';
import CheckCircleIcon from '@atlaskit/icon/core/check-circle';
import { Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { fireErrorAnalytics } from '@atlassian/error-handling';

const SUCCESS_FLAG_ID =
	'conversation-assistant-message-actions.jira-smart-issue-create.success-flag';

const messages = defineMessages({
	viewIssueCta: {
		id: 'jira-create-work-items.success-flag.view-issue-cta',
		defaultMessage: 'View{issueCount, plural, one { work item} other { work items}}',
		description: 'Action button for View Issue(s) on success flag after creating issue(s).',
	},
	copyLinkCta: {
		id: 'jira-create-work-items.success-flag.copy-link-cta',
		defaultMessage: 'Copy link',
		description: 'Action button to Copy Link for Issue(s) on success flag after creating issue(s)',
	},
	linkCopied: {
		id: 'jira-create-work-items.success-flag.link-copied',
		defaultMessage: 'Copied',
		description: 'Text to be displayed when user copies the link',
	},
});

// Creates the issue discovery link for given Jira issue keys based on the cloudUrl
const getNavigatorLink = (cloudUrl: string, issueKeys: string[]) =>
	`${cloudUrl}/issues/?jql=issuekey in (${issueKeys.join(',')})`;

export const useCreateIssuesSuccessFlags = (): (({
	createdIssueKeys,
	cloudUrl,
}: {
	createdIssueKeys: string[];
	cloudUrl?: string;
}) => void) => {
	const { formatMessage } = useIntl();
	const { showFlag } = useFlags();

	const showSuccessFlag = useCallback(
		({ createdIssueKeys, cloudUrl = '' }: { createdIssueKeys: string[]; cloudUrl?: string }) => {
			const issueDiscoveryUrl = getNavigatorLink(cloudUrl, createdIssueKeys);

			// Create a self-contained copy action that persists after component unmount
			const createFlagActions = () => {
				let isCopied = false;
				let flagId: string | null = null;

				const updateFlag = () => {
					const viewAction = {
						content: formatMessage(messages.viewIssueCta, {
							issueCount: createdIssueKeys.length,
						}),
						href: issueDiscoveryUrl,
					};
					const copyAction = {
						content: isCopied ? (
							<Text color={'color.text.success'}>{formatMessage(messages.linkCopied)}</Text>
						) : (
							formatMessage(messages.copyLinkCta)
						),
						onClick: async () => {
							try {
								await navigator.clipboard.writeText(issueDiscoveryUrl);
								isCopied = true;
								updateFlag();

								// Reset after 2 seconds
								setTimeout(() => {
									isCopied = false;
									updateFlag();
								}, 2000);

								return {
									content: formatMessage(messages.copyLinkCta),
									onClick: copyAction,
								};
							} catch (error) {
								fireErrorAnalytics({
									meta: {
										id: 'copyToClipboard',
										packageName: 'jiraSmartIssueCreate',
										teamName: 'Jira AI - Crocs',
									},
									error: error instanceof Error ? error : new Error(String(error)),
								});
							}
						},
					};

					if (flagId) {
						showFlag({
							id: flagId,
							title: formatMessage(messages.viewIssueCta, {
								issueCount: createdIssueKeys.length,
							}),
							icon: <CheckCircleIcon color={token('color.icon.success')} label="" />,
							actions: [viewAction, copyAction],
						});
					}
				};

				return {
					setFlagId: (id: string) => {
						flagId = id;
					},
					updateFlag,
				};
			};

			const { setFlagId, updateFlag } = createFlagActions();
			setFlagId(SUCCESS_FLAG_ID);
			updateFlag();
		},
		[showFlag, formatMessage],
	);

	return showSuccessFlag;
};
